import { ErrorType, type AppError } from '@/lib/errors';
import { toast } from '@/hooks/useToast';

export interface ErrorHandlingConfig {
  showToast?: boolean;
  logError?: boolean;
  retryable?: boolean;
  context?: string;
  silent?: boolean;
}

export class ErrorHandler {
  private static instance: ErrorHandler;
  
  static getInstance(): ErrorHandler {
    if (!ErrorHandler.instance) {
      ErrorHandler.instance = new ErrorHandler();
    }
    return ErrorHandler.instance;
  }

  handleError(error: unknown, config: ErrorHandlingConfig = {}): void {
    const {
      showToast = true,
      logError = true,
      context = 'Application',
      silent = false
    } = config;

    // Log error for debugging
    if (logError && !silent) {
      console.error(`Error in ${context}:`, error);
    }

    // Don't show toast if silent mode is enabled
    if (silent || !showToast) {
      return;
    }

    // Determine error details
    const errorDetails = this.getErrorDetails(error);
    
    // Show appropriate toast
    toast({
      variant: errorDetails.variant,
      title: errorDetails.title,
      description: errorDetails.description,
      duration: errorDetails.duration,
    });

    // TODO: Send to monitoring service in production
    if (process.env.NODE_ENV === 'production') {
      this.sendToMonitoring(error, context);
    }
  }

  private getErrorDetails(error: unknown) {
    if (typeof error === 'string') {
      return {
        title: 'Error',
        description: error,
        variant: 'destructive' as const,
        duration: 5000,
      };
    }

    if (error instanceof Error && 'type' in error) {
      const appError = error as AppError;
      
      switch (appError.type) {
        case ErrorType.NETWORK_ERROR:
          return {
            title: 'Network Error',
            description: 'Unable to connect to the server. Please check your connection.',
            variant: 'warning' as const,
            duration: 6000,
          };
          
        case ErrorType.AUTHENTICATION_ERROR:
          return {
            title: 'Authentication Required',
            description: 'Please log in to continue.',
            variant: 'destructive' as const,
            duration: 8000,
          };
          
        case ErrorType.SESSION_NOT_FOUND:
          return {
            title: 'Session Expired',
            description: 'Your session has expired. Please start a new conversation.',
            variant: 'warning' as const,
            duration: 6000,
          };
          
        case ErrorType.AGENT_ERROR:
          return {
            title: 'Agent Error',
            description: 'The AI agent encountered an error. Please try again.',
            variant: 'destructive' as const,
            duration: 5000,
          };
          
        case ErrorType.VALIDATION_ERROR:
          return {
            title: 'Validation Error',
            description: appError.message,
            variant: 'warning' as const,
            duration: 4000,
          };
          
        default:
          return {
            title: 'Unexpected Error',
            description: appError.message,
            variant: 'destructive' as const,
            duration: 5000,
          };
      }
    }

    if (error instanceof Error) {
      return {
        title: 'Error',
        description: error.message,
        variant: 'destructive' as const,
        duration: 5000,
      };
    }

    return {
      title: 'Unknown Error',
      description: 'An unexpected error occurred',
      variant: 'destructive' as const,
      duration: 5000,
    };
  }

  private sendToMonitoring(error: unknown, context: string): void {
    // TODO: Implement monitoring service integration
    // Example: Sentry.captureException(error, { tags: { context } });
    console.warn('Monitoring service not configured:', { error, context });
  }

  // Utility methods for common error scenarios
  handleApiError(error: unknown, endpoint?: string): void {
    this.handleError(error, {
      context: `API ${endpoint || 'Request'}`,
      showToast: true,
      logError: true,
    });
  }

  handleStreamingError(error: unknown): void {
    this.handleError(error, {
      context: 'Streaming',
      showToast: true,
      logError: true,
    });
  }

  handleSilentError(error: unknown, context?: string): void {
    this.handleError(error, {
      context: context || 'Background Operation',
      showToast: false,
      logError: true,
      silent: false,
    });
  }

  showSuccess(message: string, title = 'Success'): void {
    toast({
      variant: 'success',
      title,
      description: message,
      duration: 3000,
    });
  }

  showWarning(message: string, title = 'Warning'): void {
    toast({
      variant: 'warning',
      title,
      description: message,
      duration: 4000,
    });
  }

  showInfo(message: string, title = 'Info'): void {
    toast({
      variant: 'default',
      title,
      description: message,
      duration: 3000,
    });
  }
}

// Export singleton instance
export const errorHandler = ErrorHandler.getInstance();

// Convenience functions
export const handleError = (error: unknown, config?: ErrorHandlingConfig) => 
  errorHandler.handleError(error, config);

export const handleApiError = (error: unknown, endpoint?: string) => 
  errorHandler.handleApiError(error, endpoint);

export const handleStreamingError = (error: unknown) => 
  errorHandler.handleStreamingError(error);

export const showSuccess = (message: string, title?: string) => 
  errorHandler.showSuccess(message, title);

export const showWarning = (message: string, title?: string) => 
  errorHandler.showWarning(message, title);

export const showInfo = (message: string, title?: string) => 
  errorHandler.showInfo(message, title);